/**
 * WidgetCatalogDrawer — slide-out "Manage & Restore Widgets" panel.
 * Lists active widgets (hide / collapse controls) and hidden widgets
 * (restore → appended at the bottom of the live canvas), grouped by
 * category for the active role's view.
 */
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Minimize2, Maximize2, RotateCcw, Undo2 } from "lucide-react";

function groupByCategory(widgets) {
    const groups = {};
    for (const w of widgets) {
        const cat = w.category || "General";
        (groups[cat] = groups[cat] || []).push(w);
    }
    return Object.entries(groups);
}

export function WidgetCatalogDrawer({
    open, onOpenChange, widgets, hidden, minimized,
    onRestore, onRestoreAll, onHide, onToggleMinimize, onReset,
}) {
    const hiddenWidgets = widgets.filter((w) => hidden.includes(w.id));
    const activeWidgets = widgets.filter((w) => !hidden.includes(w.id));

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="widget-catalog-drawer">
                <SheetHeader className="text-left">
                    <SheetTitle className="font-heading">Manage &amp; Restore Widgets</SheetTitle>
                    <SheetDescription>
                        Drag, resize, collapse or remove widgets on the canvas. Removed widgets land here — restore them any time.
                    </SheetDescription>
                </SheetHeader>

                {hiddenWidgets.length > 0 && (
                    <div className="mt-5">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">
                                Hidden · {hiddenWidgets.length}
                            </span>
                            <Button size="sm" variant="outline" onClick={onRestoreAll} data-testid="restore-all-widgets-btn">
                                <Undo2 className="w-3.5 h-3.5 mr-1" /> Restore all
                            </Button>
                        </div>
                        {groupByCategory(hiddenWidgets).map(([cat, ws]) => (
                            <div key={cat} className="mb-3">
                                <div className="text-[10px] font-semibold text-muted-foreground mb-1">{cat}</div>
                                <div className="space-y-1.5">
                                    {ws.map((w) => (
                                        <div key={w.id} className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 px-3 py-2" data-testid={`catalog-hidden-${w.id}`}>
                                            <span className="text-sm font-medium flex items-center gap-2">
                                                <EyeOff className="w-3.5 h-3.5 text-muted-foreground" /> {w.title}
                                            </span>
                                            <Button size="sm" variant="secondary" onClick={() => onRestore(w.id)} data-testid={`restore-widget-${w.id}`}>
                                                Restore
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="mt-5">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">
                        On canvas · {activeWidgets.length}
                    </span>
                    {groupByCategory(activeWidgets).map(([cat, ws]) => (
                        <div key={cat} className="mt-2 mb-3">
                            <div className="text-[10px] font-semibold text-muted-foreground mb-1">{cat}</div>
                            <div className="space-y-1.5">
                                {ws.map((w) => {
                                    const isMin = minimized.includes(w.id);
                                    return (
                                        <div key={w.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2" data-testid={`catalog-active-${w.id}`}>
                                            <span className="text-sm font-medium flex items-center gap-2">
                                                <Eye className="w-3.5 h-3.5 text-muted-foreground" /> {w.title}
                                            </span>
                                            <div className="flex items-center gap-1">
                                                <Button
                                                    size="icon" variant="ghost" className="h-7 w-7"
                                                    title={isMin ? "Expand" : "Collapse"}
                                                    onClick={() => onToggleMinimize(w.id)}
                                                    data-testid={`catalog-minimize-${w.id}`}
                                                >
                                                    {isMin ? <Maximize2 className="w-3.5 h-3.5" /> : <Minimize2 className="w-3.5 h-3.5" />}
                                                </Button>
                                                <Button
                                                    size="icon" variant="ghost" className="h-7 w-7 hover:text-rose-500"
                                                    title="Hide widget"
                                                    onClick={() => onHide(w.id)}
                                                    data-testid={`catalog-hide-${w.id}`}
                                                >
                                                    <EyeOff className="w-3.5 h-3.5" />
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-6 border-t border-border pt-4">
                    <Button variant="outline" size="sm" onClick={onReset} data-testid="layout-reset-btn">
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset layout to default
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}

export default WidgetCatalogDrawer;
